// Duitku Configuration for TraderTekno
// Redirect-based payment flow (no popup/script injection)
import { getAPIUrl } from './api.config';

export interface DuitkuConfig {
  environment: 'sandbox' | 'production';
  merchantCode: string;
  callbackUrl: string;
  returnUrl: string;
}

// Get environment-specific configuration
export const getDuitkuConfig = (): DuitkuConfig => {
  const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';
  const baseUrl = (import.meta.env.VITE_APP_URL as string | undefined) ?? 'http://localhost:3000';
  const apiUrl = getAPIUrl();

  return {
    environment: isProduction ? 'production' : 'sandbox',
    merchantCode: isProduction
      ? (import.meta.env.VITE_DUITKU_MERCHANT_CODE as string)
      : (import.meta.env.VITE_DUITKU_MERCHANT_CODE_SANDBOX as string | undefined) ?? '',
    callbackUrl: `${apiUrl}/api/payments/callback`,
    returnUrl: `${baseUrl}/payment/success`,
  };
};

// Helper to format currency to IDR
export const formatIDR = (amount: number): string => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Transaction status mapping with Indonesian translations
export const TRANSACTION_STATUS = {
  SUCCESS: 'success',
  PENDING: 'pending',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
} as const;

export type TransactionStatus = typeof TRANSACTION_STATUS[keyof typeof TRANSACTION_STATUS];

// Map transaction status to user-friendly messages
export const getStatusMessage = (status: TransactionStatus, lang: 'en' | 'id' = 'id'): string => {
  const messages = {
    en: {
      [TRANSACTION_STATUS.SUCCESS]: 'Payment successful',
      [TRANSACTION_STATUS.PENDING]: 'Payment pending',
      [TRANSACTION_STATUS.FAILED]: 'Payment failed',
      [TRANSACTION_STATUS.CANCELLED]: 'Payment cancelled',
      [TRANSACTION_STATUS.EXPIRED]: 'Payment expired',
    },
    id: {
      [TRANSACTION_STATUS.SUCCESS]: 'Pembayaran berhasil',
      [TRANSACTION_STATUS.PENDING]: 'Pembayaran menunggu',
      [TRANSACTION_STATUS.FAILED]: 'Pembayaran gagal',
      [TRANSACTION_STATUS.CANCELLED]: 'Pembayaran dibatalkan',
      [TRANSACTION_STATUS.EXPIRED]: 'Pembayaran kadaluarsa',
    }
  };

  return messages[lang][status] || status;
};

// Map Duitku result code to TransactionStatus
export const mapResultCode = (resultCode: string): TransactionStatus => {
  switch (resultCode) {
    case '00': return TRANSACTION_STATUS.SUCCESS;
    case '01': return TRANSACTION_STATUS.FAILED;
    case '02': return TRANSACTION_STATUS.CANCELLED;
    default: return TRANSACTION_STATUS.PENDING;
  }
};
